import { ColumnProps } from "antd/lib/table";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Tag } from "antd";
import { CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import ZoneTypeContext from "../../../../context/wms/zone-type/context";
import { IZoneType } from "../../../../models/wms/zone-type/response";
import Space from "../../../general/antd/space";
import ViewBtn from "../../../general/table-components/actions/view-btn";
import EditBtn from "../../../general/table-components/actions/edit-btn";
import DeleteBtn from "../../../general/table-components/actions/delete-btn";
import { ISort, IStaticFilter } from "../../../../models/base/base-query";
import { tableOnChange } from "../../../../utils/helpers/table-sorts-filters";
import Table from "../../../general/table-components/table";
import styles from "./styles.module.scss";

interface IProps {}

const ZoneTypeTable: React.FC<IProps> = (props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { list, actions, loading, query } = useContext(ZoneTypeContext);

  const columns: ColumnProps<IZoneType>[] = [
    {
      title: t("id"),
      dataIndex: "id",
      key: "id",
      sorter: {
        multiple: 1,
      },
      width: 80,
      align: "center",
    },
    {
      title: t("name"),
      dataIndex: "name",
      align: "center",
      key: "name",
      width: 200,
      sorter: {
        multiple: 2,
      },
      render: (name: string) => name || "-",
    },
    {
      title: t("zones_count"),
      dataIndex: "zonesCount",
      align: "center",
      key: "zonesCount",
      width: 120,
      sorter: {
        multiple: 3,
      },
      render: (value: number) => value ?? "-",
    },
    {
      title: t("is_active"),
      dataIndex: "isActive",
      align: "center",
      key: "isActive",
      sorter: {
        multiple: 4,
      },
      width: 120,
      render: (value: boolean) =>
        value ? (
          <Tag icon={<CheckCircleOutlined />} color="success">
            {t("active")}
          </Tag>
        ) : (
          <Tag icon={<CloseCircleOutlined />} color="default">
            {t("inactive")}
          </Tag>
        ),
    },
    {
      title: t("created_at"),
      dataIndex: "createdAt",
      align: "center",
      key: "createdAt",
      sorter: {
        multiple: 5,
      },
      width: 150,
      render: (value: Date) => value ? new Date(value).toLocaleDateString() : "-",
    },
    {
      title: t("actions"),
      width: 200,
      dataIndex: "",
      align: "center",
      key: "actions",
      render: (_: any, record) => (
        <Space>
          <ViewBtn
            onClick={(e) => {
              const url = `/wms/zone-types/details/${record?.id}`;
              if (e.ctrlKey || e.metaKey) {
                window.open(url, "_blank");
              } else {
                navigate(url);
              }
            }}
          />
          <EditBtn
            onClick={(e) => {
              const url = `/wms/zone-types/update/${record?.id}`;
              if (e.ctrlKey || e.metaKey) {
                window.open(url, "_blank");
              } else {
                navigate(url);
              }
            }}
          />
          <DeleteBtn
            onConfirm={async () => {
              await actions.deleteZoneType(record?.id);
            }}
            loading={loading.includes("delete")}
          />
        </Space>
      ),
    },
  ];

  // Filters && Sorts
  let tableFiltersProps = {};
  const [sorts, setSorts] = useState<ISort[]>([]);
  const [, setStaticFilters] = useState<IStaticFilter[]>(
    query?.filters ?? []
  );

  useEffect(() => {
    sorts.length > 0 &&
      actions.setQuery({
        ...query,
        orders: sorts,
      });
  }, [sorts]);

  return (
    <>
      <Table<any>
        rowKey="id"
        showPagination={true}
        columns={columns}
        dataSource={list?.data ?? []}
        loading={loading.includes("list") || list == undefined}
        size="small"
        className={styles.table}
        total={list?.total}
        pageSize={query.perPage}
        page={query.page}
        onPaginationChange={(page, pageSize) => {
          actions.setQuery({
            ...query,
            page,
            perPage: pageSize,
          });
        }}
        onChange={(_, tableFilters, tabelSorters) => {
          tableOnChange(
            tableFilters,
            tabelSorters,
            tableFiltersProps,
            setSorts,
            setStaticFilters
          );
        }}
      />
    </>
  );
};

export default ZoneTypeTable;
